import React, { useState, useRef, useCallback } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import imageCompression from 'browser-image-compression';

const ImageUploadPreview = ({
  initialUrls = [],
  onChange,
  uploadImageFn,
  onFullyUploaded,
  onUploadingChange,
  maxImages = Infinity,
}) => {
  const [imageUrls, setImageUrls] = useState(Array.isArray(initialUrls) ? initialUrls : []);
  const fileInputRef = useRef(null);
  const [uploadingCount, setUploadingCount] = useState(0);
  const notifyUploadingChange = useCallback((count) => {
    if (typeof onUploadingChange === 'function') {
      onUploadingChange(count > 0);
    }
  }, [onUploadingChange]);

  React.useEffect(() => {
    setImageUrls((prev) => {
      const nextRaw = Array.isArray(initialUrls) ? initialUrls : [];
      const next =
        maxImages === Infinity
          ? nextRaw
          : nextRaw.slice(Math.max(0, nextRaw.length - maxImages));
      const isSame =
        prev.length === next.length &&
        prev.every((url, idx) => url === next[idx]);
      return isSame ? prev : next;
    });
  }, [initialUrls, maxImages]);

  React.useEffect(() => {
    if (onChange) onChange(imageUrls);
  }, [imageUrls, onChange]);
  const safeUrls = imageUrls;
  const [uploadProgress, setUploadProgress] = useState({});

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    const currentUrls = Array.isArray(imageUrls) ? [...imageUrls] : [];
    const tempUploads = [];
    if (!files.length) return;

    // Show temp previews immediately
    const newTempUrls = files.map(file => {
      const tempId = URL.createObjectURL(file);
      tempUploads.push({ tempId, file });
      setUploadProgress(prev => ({ ...prev, [tempId]: 0 }));
      return tempId;
    });

    let newUrls = [...currentUrls, ...newTempUrls];
    if (maxImages !== Infinity && newUrls.length > maxImages) {
      newUrls = newUrls.slice(newUrls.length - maxImages);
    }
    setImageUrls(newUrls);
    setUploadingCount((prev) => {
      const next = prev + tempUploads.length;
      notifyUploadingChange(next);
      return next;
    });

    for (const { tempId, file } of tempUploads) {
      try {
        const compressedFile = await imageCompression(file, {
          maxSizeMB: 1,
          maxWidthOrHeight: 1024,
          useWebWorker: true
        });
        const realUrl = await uploadImageFn(compressedFile, (percent) => {
          const safePercent = Math.min(99, Math.round(percent));
          setUploadProgress(prev => ({ ...prev, [tempId]: safePercent }));
        });

        setImageUrls(prev => {
          let updated = prev.filter(url => url !== tempId);
          updated = [...updated, realUrl];
          if (maxImages !== Infinity && updated.length > maxImages) {
            updated = updated.slice(updated.length - maxImages);
          }
          return updated;
        });
        if (onFullyUploaded) onFullyUploaded(realUrl);
      } catch (error) {
        console.error('Image upload failed for:', file.name, error);
      } finally {
        setUploadProgress(prev => {
          const { [tempId]: _, ...rest } = prev;
          return rest;
        });
      }
      setUploadingCount((prev) => {
        const next = Math.max(0, prev - 1);
        notifyUploadingChange(next);
        return next;
      });
    }
  };

  const handleRemoveImage = (index) => {
    if (!Array.isArray(imageUrls)) return;
    const updated = imageUrls.filter((_, i) => i !== index);
    setImageUrls(updated);
  };

  return (
    <Box>
      <Box
        onDrop={e => {
          e.preventDefault();
          handleFileChange({ target: { files: e.dataTransfer.files } });
        }}
        onDragOver={e => e.preventDefault()}
        sx={{
          border: '2px dashed #ccc',
          borderRadius: 2,
          padding: 2,
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: '#fafafa'
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <Typography variant="body2">Click or drag images here to upload</Typography>
        <input
          ref={fileInputRef}
          type="file"
          multiple={maxImages !== 1}
          accept="image/*"
          onChange={handleFileChange}
          hidden
        />
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
        {safeUrls.map((src, idx) => {
          const progress = uploadProgress[src];
          const isTemp = src.startsWith('blob:') || typeof progress === 'number';
          return (
            <Box key={idx} sx={{ position: 'relative' }}>
              <img
                src={src}
                alt={`Preview ${idx}`}
                onError={(e) => {
                  console.warn('Failed to load image:', src);
                  e.target.style.display = 'none';
                }}
                style={{ width: 160, height: 100, objectFit: 'cover', borderRadius: 6 }}
              />
              {isTemp && progress !== undefined && (
                <Box sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backdropFilter: 'blur(2px)',
                  backgroundColor: 'rgba(255,255,255,0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  zIndex: 1,
                  flexDirection: 'column',
                }}>
                  <CircularProgress
                    variant="determinate"
                    value={Math.min(progress, 99)}
                    size={48}
                    thickness={3}
                    sx={{ color: '#1976d2', mb: 1 }}
                  />
                  <Typography variant="caption" fontWeight="bold" color="textSecondary">
                    {progress >= 100 ? 'Finalizing…' : `${progress}%`}
                  </Typography>
                </Box>
              )}
              <Button
                onClick={() => handleRemoveImage(idx)}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  width: 24,
                  height: 24,
                  minWidth: 0,
                  padding: 0,
                  backgroundColor: 'white',
                  color: 'red',
                  fontWeight: 'bold',
                  borderRadius: '50%',
                  lineHeight: 1
                }}
              >
                ✕
              </Button>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ImageUploadPreview;
